import {
  type EmbedPlatform,
  SOCIAL_PLATFORM_LABEL,
  type SocialLink,
  type SocialPlatform,
} from '@fontbold/ui';
import type { SocialPlatform as ApiSocialPlatform } from '#/lib/gql/graphql';
import type { Space } from '#/lib/queries/space';

const FALLBACK_SITE_NAME = 'FontBold';

const SHOWN_PLATFORMS: Partial<Record<ApiSocialPlatform, SocialPlatform>> = {
  Instagram: 'instagram',
  TikTok: 'tiktok',
  YouTube: 'youtube',
  Substack: 'substack',
};

export function siteTitle(space: Space | null | undefined): string {
  return space?.metaTitle || space?.name || FALLBACK_SITE_NAME;
}

export function pageTitle(
  page: string,
  space: Space | null | undefined,
): string {
  return `${page} | ${space?.name || FALLBACK_SITE_NAME}`;
}

export function socialLinks(space: Space): SocialLink[] {
  return space.socialLinks.flatMap((link) => {
    const platform = link ? SHOWN_PLATFORMS[link.platform] : undefined;
    if (!link || !platform) return [];

    return [
      {
        platform,
        href: link.url,
        label: SOCIAL_PLATFORM_LABEL[platform],
      },
    ];
  });
}

export function showsReels(space: Space): boolean {
  return space.homepage.includes('Reels');
}

export function embedPlatform(
  format: string | null | undefined,
): EmbedPlatform | undefined {
  if (format?.startsWith('instagram')) return 'instagram';
  if (format?.startsWith('tiktok')) return 'tiktok';
  if (format?.startsWith('youtube')) return 'youtube';
  return undefined;
}

const HANDLE_PATTERNS: Record<EmbedPlatform, RegExp> = {
  instagram: /instagram\.com\/(?!p\/|reel\/|reels\/|tv\/)([A-Za-z0-9._]+)/,
  tiktok: /tiktok\.com\/@([A-Za-z0-9._]+)/,
  youtube: /youtube\.com\/@([A-Za-z0-9._-]+)/,
};

function handleFrom(url: string, platform: EmbedPlatform): string | null {
  return url.match(HANDLE_PATTERNS[platform])?.[1] ?? null;
}

export function embedHandle(
  url: string | null | undefined,
  platform: EmbedPlatform,
  space: Space,
): string {
  const fromEmbed = url ? handleFrom(url, platform) : null;
  if (fromEmbed) return fromEmbed;

  const profile = socialLinks(space).find((link) => link.platform === platform);

  return profile ? (handleFrom(profile.href, platform) ?? '') : '';
}
